'use client';

import { motion } from 'framer-motion';
import styles from './animatedBackground.module.scss';
import { BackgroundKey } from './TopBackground';

// Note: Animation WIP
type AnimatedBackgroundProps = Readonly<{
  backgroundKey: BackgroundKey;
}>;

const fallBackAnimationValues = {
  background: '#F4F0ED',
  blob1: '#8E7FBB',
  blob1Opacity: 0.3,
  blob2: undefined,
  blob2Opacity: undefined,
  circleGradientStart: 'white',
  circleGradientMid: 'white',
  circleGradientEnd: 'white'
};

const COLOR_VARIANTS = {
  'product-hero-1': {
    background: '#F4F0ED',
    blob1: '#8E7FBB',
    blob1Opacity: 0.4,
    blob2: '#E4CEB7',
    blob2Opacity: 0.3,
    blob3: '#3A718A',
    blob3Opacity: 0.3,
    circleGradientStart: 'white',
    circleGradientMid: 'white',
    circleGradientEnd: 'white'
  },
  'product-hero-2': {
    background: '#F4F0ED',
    blob1: '#B9A7EE',
    blob1Opacity: 0.4,
    blob2: '#676279',
    blob2Opacity: 0.2,
    circleGradientStart: '#A69CC6',
    circleGradientMid: '#A69CC6',
    circleGradientEnd: '#A69CC6'
  },
  'product-hero-3': {
    background: '#F4F0ED',
    blob1: '#A69CC6',
    blob1Opacity: 0.3,
    blob2: '#45396A',
    blob2Opacity: 1,
    circleGradientStart: '#9CC6BE',
    circleGradientMid: '#9CC6BE',
    circleGradientEnd: '#9CC6BE'
  },
  'product-hero-4': {
    background: '#F4F0ED',
    blob1: '#E8E5E1',
    blob1Opacity: 0.7,
    blob2: '#C9B49D',
    blob2Opacity: 0.4,
    blob3: '#3A718A',
    blob3Opacity: 0.15,
    circleGradientStart: 'white',
    circleGradientMid: 'white',
    circleGradientEnd: 'white'
  },
  'product-hero-5': {
    background: '#F4F0ED',
    blob1: '#8E7FBB',
    blob1Opacity: 0.3,
    blob2: undefined,
    blob2Opacity: undefined,
    circleGradientStart: 'white',
    circleGradientMid: 'white',
    circleGradientEnd: 'white'
  },
  'product-hero-6': {
    background: '#1A182B',
    blob1: '#45396A',
    blob1Opacity: 1,
    blob2: '#3A718A',
    blob2Opacity: 0.7,
    circleGradientStart: 'white',
    circleGradientMid: 'white',
    circleGradientEnd: 'white'
  },
  'solution-hero-1': fallBackAnimationValues,
  'solution-hero-2': fallBackAnimationValues,
  'solution-hero-3': fallBackAnimationValues,
  'landing-hero': fallBackAnimationValues
} as const;

export default function AnimatedBackground({
  backgroundKey = 'product-hero-1'
}: AnimatedBackgroundProps) {
  if (!backgroundKey) return null;

  const colors = COLOR_VARIANTS[backgroundKey];

  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 2000 920"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.animatedSvg}
      preserveAspectRatio="xMidYMid slice"
    >
      <g clipPath="url(#clip0)">
        <rect width="2000" height="920" fill={colors.background} />

        {/* Animated blob 1 */}
        <g filter="url(#filter0_f)">
          <motion.path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M1178.33 398.794C916.954 361.792 653.918 222.84 507.895 84.3539C369.023 -47.3492 404.746 -173.071 459.219 -283.193C507.591 -380.978 638.368 -438.896 791.015 -487.991C985.749 -550.621 1160.32 -653.095 1443.96 -599.906C1748.98 -542.708 2137.77 -384.371 2202.49 -223.311C2268.79 -58.3201 1834.44 -66.4053 1704.32 47.9837C1643.51 101.435 1738.28 200.11 1666.02 248.31C1545.77 328.51 1419.5 432.937 1178.33 398.794Z"
            fill={colors.blob1}
            fillOpacity={colors.blob1Opacity ?? 1}
            animate={{
              scale: [1, 1.05, 0.98, 1.03, 1],
              opacity: [1, 0.85, 1, 0.9, 1]
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
            style={{ transformOrigin: 'center' }}
          />
        </g>

        {/* Animated blob 2 - only render if blob2 exists */}
        {colors.blob2 && (
          <g opacity={colors.blob2Opacity ?? 0.7} filter="url(#filter1_f)">
            <motion.path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M186.141 192.275C-145.039 155.203 -511.363 233.853 -734.651 347.455C-947.003 455.492 -944.216 609.654 -913.465 751.717C-886.159 877.865 -745.279 983.552 -574.826 1085.81C-357.379 1216.26 -177.495 1386.02 186.141 1411.4C577.177 1438.68 1103.35 1376.06 1234.7 1212.07C1369.24 1044.08 836.223 921.584 714.486 751.717C657.602 672.343 805.375 588.586 732.782 511.721C611.995 383.825 491.723 226.482 186.141 192.275Z"
              fill={colors.blob2}
              animate={{
                scale: [1, 0.96, 1.04, 0.98, 1],
                opacity: [0.7, 0.85, 0.6, 0.75, 0.7]
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 3
              }}
              style={{ transformOrigin: 'center' }}
            />
          </g>
        )}

        {/* Large outer circle - slow rotation */}
        <motion.circle
          cx="627.618"
          cy="1122.56"
          r="1122.62"
          fill="url(#paint0_linear)"
          fillOpacity="0.25"
          animate={{
            rotate: 360,
            scale: [1, 1.02, 0.98, 1],
            opacity: [0.7, 0.55, 0.25, 0.55, 0.7]
          }}
          transition={{
            rotate: {
              duration: 40,
              repeat: Infinity,
              ease: 'linear'
            },
            scale: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut'
            },
            opacity: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut'
            }
          }}
          style={{ transformOrigin: '627.618px 1122.56px' }}
        />

        {/* Inner circle - counter-rotation */}
        <motion.circle
          cx="627.837"
          cy="1122.93"
          r="660.952"
          fill="url(#paint1_linear)"
          fillOpacity="0.25"
          initial={{ rotate: -28.4197 }}
          animate={{
            rotate: [-28.4197, 331.5803],
            scale: [1, 0.97, 1.04, 1],
            opacity: [0.7, 0.55, 0.25, 0.55, 0.7]
          }}
          transition={{
            rotate: {
              duration: 30,
              repeat: Infinity,
              ease: 'linear'
            },
            scale: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2
            },
            opacity: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }
          }}
          style={{ transformOrigin: '627.837px 1122.93px' }}
        />
      </g>

      <defs>
        <filter
          id="filter0_f"
          x="11.2917"
          y="-1014.39"
          width="2597.72"
          height="1819.52"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="199.839"
            result="effect1_foregroundBlur_1593_8133"
          />
        </filter>
        <filter
          id="filter1_f"
          x="0"
          y="-127.857"
          width="1200"
          height="1856.29"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="155.431"
            result="effect1_foregroundBlur_1593_8133"
          />
        </filter>
        <linearGradient
          id="paint0_linear"
          x1="1750.24"
          y1="1122.56"
          x2="627.618"
          y2="1122.56"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colors.circleGradientStart} />
          <stop
            offset="0.461021"
            stopColor={colors.circleGradientMid}
            stopOpacity="0.24"
          />
          <stop
            offset="1"
            stopColor={colors.circleGradientEnd}
            stopOpacity="0.24"
          />
        </linearGradient>
        <linearGradient
          id="paint1_linear"
          x1="1288.79"
          y1="1122.93"
          x2="627.837"
          y2="1122.93"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colors.circleGradientStart} />
          <stop
            offset="1"
            stopColor={colors.circleGradientEnd}
            stopOpacity="0"
          />
        </linearGradient>
        <clipPath id="clip0">
          <rect width="2000" height="920" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}
